// utils/authRedirectGuard.js
// T2.3 — pure decision predicate for the app-level auth redirect.
//
// Context: src/supabase.js registers a module-level onAuthStateChange listener
// that sends signed-out visitors to /dashboard. Stripe checkout returns the
// customer to the SITE ROOT carrying ?payment=... params; if INITIAL_SESSION
// fires before the session rehydrates (expired refresh token, storage loss in
// privacy/in-app browsers), the old redirect stripped those params and the
// return verification / cancel handling never ran — a low-frequency,
// high-severity latent bug on the money path.
//
// This predicate preserves the existing redirect behavior EXACTLY, with two
// added bail-outs: never redirect while a Stripe return is in flight
// (search contains "payment="), and never redirect while an OAuth / email-link
// auth callback is in flight (callback params in the query or the hash). Pure
// function, no imports, no side effects.
//
// Original IP — LoadDash Services.

/**
 * True when a URL fragment (query string OR location hash) carries the
 * single-use params of an auth callback the Supabase client still needs to
 * consume: an implicit-flow token (#access_token=), a PKCE code (?code=), or a
 * provider error. Accepts a leading "?" or "#" or a bare "a=b" fragment.
 *
 * @param {string} fragment - a window.location.search or .hash value
 * @returns {boolean}
 */
function hasAuthCallbackParams(fragment) {
  if (typeof fragment !== "string" || !fragment) return false;
  // URLSearchParams strips a leading "?" but not "#"; normalize the hash form.
  const raw = fragment.charAt(0) === "#" ? fragment.slice(1) : fragment;
  const params = new URLSearchParams(raw);
  return params.has("access_token") || params.has("code") || params.has("error");
}

/**
 * Decide whether the app-level auth listener should redirect to /dashboard.
 *
 * @param {object} args
 * @param {string} args.event     - Supabase auth event name
 * @param {object|null} args.session - session object from the event (may be null)
 * @param {string} args.pathname  - window.location.pathname
 * @param {string} args.search    - window.location.search
 * @param {string} args.hash      - window.location.hash
 * @returns {boolean} true only when the pre-existing redirect should fire
 */
export function shouldRedirectToDashboard({ event, session, pathname, search, hash } = {}) {
  // Original trigger condition, unchanged: signed out, or startup with no session.
  const signedOutish = event === "SIGNED_OUT" || (event === "INITIAL_SESSION" && !session);
  if (!signedOutish) return false;

  // T2.3 bail-out: a Stripe return is in flight — the ?payment= params MUST
  // reach App.modular's return handler (verification / cancel marking).
  if (typeof search === "string" && search.includes("payment=")) return false;

  // Auth-callback bail-out: an OAuth (Google) return or an email confirmation
  // link is in flight. The Supabase client must consume the callback params to
  // establish the session — implicit-flow tokens land in the hash
  // (#access_token=), PKCE codes in the query (?code=), and provider errors in
  // either. A full-page redirect here strips those single-use params before the
  // client reads them, so the sign-in silently fails and the visitor bounces
  // back to the login screen (having to click the provider a second time).
  // Same class of bug as the payment= bail-out above.
  if (hasAuthCallbackParams(search) || hasAuthCallbackParams(hash)) return false;

  // Original exclusions, unchanged: hauler surface and already-at-target.
  const path = typeof pathname === "string" ? pathname : "";
  if (path.startsWith("/haulers")) return false;
  if (path === "/dashboard") return false;

  return true;
}
